import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, notification, Select } from 'antd';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import ApiService from 'utils/ApiCalls';
import { FilterKey, ProcessRow } from 'store/ducks/InternalControls/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getProcessColumns } from 'constants/processColumns';
import { internalControlsOp } from 'store/ducks/InternalControls';
import * as Styled from './Processes.styled';

const { Option } = Select;
const FiltersParam = withDefault(ArrayParam, []);

const FILTER_CONFIG: Record<
  FilterKey,
  { field: keyof ProcessRow; nestedField?: string }
> = {
  title: { field: 'title' },
  description: { field: 'description' },
  activation: { field: 'activation_date' },
  type: { field: 'process_type', nestedField: 'name' },
  responsible: { field: 'responsible_person' },
  version: { field: 'version' },
  comments: { field: 'comments' },
  owner: { field: 'owner_maintainer' }
};

const Processes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  // Sync language with i18n
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam
  });

  const [processes, setProcesses] = useState<ProcessRow[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const originalProcessesRef = useRef<ProcessRow[]>([]);
  const [newProcessIds, setNewProcessIds] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    entity_id: selectedEntityId,
    group_id: undefined as number | undefined,
    division_id: undefined as number | undefined,
    region_id: undefined as number | undefined,
    legal_entity_id: undefined as number | undefined,
    business_unit_id: undefined as number | undefined
  });

  type SearchState = { search: string; showSearch: boolean };

  const [searchStates, setSearchStates] = useState<
    Record<FilterKey, SearchState>
  >({
    title: { search: '', showSearch: false },
    description: { search: '', showSearch: false },
    activation: { search: '', showSearch: false },
    type: { search: '', showSearch: false },
    responsible: { search: '', showSearch: false },
    version: { search: '', showSearch: false },
    comments: { search: '', showSearch: false },
    owner: { search: '', showSearch: false }
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, entity_id: selectedEntityId }));
  }, [selectedEntityId]);
  const dispatch = useDispatch();

  const trackingSetProcesses = useCallback(
    (newProcesses: ProcessRow[]) => {
      setProcesses(newProcesses);
      setIsDirty(true);
      dispatch(internalControlsOp.setHasChanges(true));
    },
    [dispatch]
  );

  useEffect(() => {
    setSaveDisabled(!isDirty && newProcessIds.size === 0);
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newProcessIds.size > 0)
    );
  }, [isDirty, newProcessIds, dispatch]);

  const [processType, setProcessType] = useState<
    { label: string; value: number }[]
  >([]);
  const [business_process_l3, setBusinessProcessL3] = useState<
    { label: string; value: number }[]
  >([]);
  const [group, setGroup] = useState<{ label: string; value: number }[]>([]);
  const [division, setDivision] = useState<{ label: string; value: number }[]>(
    []
  );
  const [region, setRegion] = useState<{ label: string; value: number }[]>([]);
  const [legal_entity, setLegalEntities] = useState<
    { label: string; value: number }[]
  >([]);
  const [business_unit, setBusinessUnit] = useState<
    { label: string; value: number }[]
  >([]);
  const [process_criticality, setProcessCriticality] = useState<
    { label: string; value: number }[]
  >([]);

  const scopeKey = useMemo(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) => value !== undefined && value !== null
      )
    );

    return JSON.stringify(cleanFilters);
  }, [filters]);

  const handleDisplay = async () => {
    try {
      const filtersWithEntity = {
        ...filters,
        entity_id: selectedEntityId
      };

      // Remove undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(filtersWithEntity).filter(
          ([_, value]) => value !== undefined
        )
      );

      const response = await ApiService.getBusinessProcessL4BPR(cleanFilters);
      const apiRisks = response.data.data;
      const mappedProcesses = apiRisks.map((item: any) => ({
        key: item.id.toString(),
        id: item.id,
        title: item.title,
        description: item.description,
        description1: item.description1,
        description2: item.description2,
        process_image: item.process_image,
        process_risk: item.process_risk,
        responsible_person: item.responsible_person,
        start_date: item.start_date,
        end_date: item.end_date,
        activation_date: item.activation_date,
        active: item.active,
        deleted: item.deleted,
        version: item.version,
        comments: item.comments,
        owner_maintainer: item.owner_maintainer,
        created_at: item.created_at,
        updated_at: item.updated_at,

        process_type: {
          id: item.process_type?.id ?? null,
          name: item.process_type?.name ?? null
        },

        business_process_l3: {
          id: item.business_process_l3?.id ?? null,
          title: item.business_process_l3?.title ?? null
        },

        group: {
          id: item.group?.id ?? null,
          title: item.group?.name ?? null
        },

        division: {
          id: item.division?.id ?? null,
          title: item.division?.name ?? null
        },

        region: {
          id: item.region?.id ?? null,
          title: item.region?.name ?? null
        },

        legal_entity: {
          id: item.legal_entity?.id ?? null,
          title: item.legal_entity?.name ?? null
        },

        business_unit: {
          id: item.business_unit?.id ?? null,
          title: item.business_unit?.name ?? null
        },

        process_criticality: {
          id: item.process_criticality?.id ?? null,
          title: item.process_criticality?.name ?? null
        }
      }));

      setProcesses(mappedProcesses);
      originalProcessesRef.current = JSON.parse(
        JSON.stringify(mappedProcesses)
      );
      setIsDirty(false);
    } catch (error) {
      console.error('Error fetching processes:', error);
    }
  };

  useEffect(() => {
    handleDisplay();
  }, [drawTable, scopeKey]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          processTypeRes,
          businessprocessL3Res,
          groupRes,
          divisionRes,
          regionRes,
          legalEntitiesRes,
          businessUnitRes,
          processCriticalityRes
        ] = await Promise.all([
          ApiService.getPorcessTypeBPR(),
          ApiService.getBusinessProcessL3BPR(selectedEntityId),
          ApiService.getGroupBPR(),
          ApiService.getDivisionBPR(),
          ApiService.getRegionBPR(),
          ApiService.getLegalEntitiesBPR(),
          ApiService.getBusinessUnitBPR(),
          ApiService.getProcessCriticalitiesBPR()
        ]);

        setProcessType(
          processTypeRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setBusinessProcessL3(
          businessprocessL3Res.data.data.map((type: any) => ({
            label: type.title,
            value: type.id
          }))
        );

        setGroup(
          groupRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setDivision(
          divisionRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setRegion(
          regionRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setLegalEntities(
          legalEntitiesRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setBusinessUnit(
          businessUnitRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );

        setProcessCriticality(
          processCriticalityRes.data.data.map((type: any) => ({
            label: type.name,
            value: type.id
          }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const risksColumns = useMemo(
    () =>
      getProcessColumns(
        process_criticality,
        processes,
        processType,
        group,
        division,
        region,
        legal_entity,
        business_unit,
        business_process_l3,
        trackingSetProcesses,
        true,
        true,
        'business_process_l3',
        t ? t('BUSINESS_PROCESS_LEVEL_3') : 'Business Process Level 3',
        'L4',
        newProcessIds,
        {
          title: {
            search: searchStates.title.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                title: { ...prev.title, search: val }
              })),
            showSearch: searchStates.title.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                title: { ...prev.title, showSearch: val }
              }))
          },
          description: {
            search: searchStates.description.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                description: { ...prev.description, search: val }
              })),
            showSearch: searchStates.description.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                description: { ...prev.description, showSearch: val }
              }))
          },
          activation: {
            search: searchStates.activation.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                activation: { ...prev.activation, search: val }
              })),
            showSearch: searchStates.activation.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                activation: { ...prev.activation, showSearch: val }
              }))
          },
          type: {
            search: searchStates.type.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                type: { ...prev.type, search: val }
              })),
            showSearch: searchStates.type.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                type: { ...prev.type, showSearch: val }
              }))
          },
          responsible: {
            search: searchStates.responsible.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                responsible: { ...prev.responsible, search: val }
              })),
            showSearch: searchStates.responsible.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                responsible: { ...prev.responsible, showSearch: val }
              }))
          },
          version: {
            search: searchStates.version.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                version: { ...prev.version, search: val }
              })),
            showSearch: searchStates.version.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                version: { ...prev.version, showSearch: val }
              }))
          },
          comments: {
            search: searchStates.comments.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                comments: { ...prev.comments, search: val }
              })),
            showSearch: searchStates.comments.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                comments: { ...prev.comments, showSearch: val }
              }))
          },
          owner: {
            search: searchStates.owner.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                owner: { ...prev.owner, search: val }
              })),
            showSearch: searchStates.owner.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                owner: { ...prev.owner, showSearch: val }
              }))
          }
        },
        t
      ),
    [
      processes,
      processType,
      newProcessIds,
      process_criticality,
      group,
      division,
      region,
      legal_entity,
      business_unit,
      business_process_l3,
      trackingSetProcesses,
      searchStates,
      t
    ]
  );

  const handleAddProcessRow = () => {
    const getQueryValue = (key: keyof typeof query): number | null => {
      const value = query[key]?.[0];
      return value !== undefined && value !== null ? Number(value) : null;
    };

    const newId = generateUniqueId();
    const newRow: ProcessRow = {
      id: newId,
      key: (processes.length + 1).toString(),
      title: '',
      description: '',
      description1: '',
      description2: '',
      process_image: '',
      process_risk: '',
      responsible_person: '',
      start_date: '',
      end_date: '',
      activation_date: '',
      active: '',
      deleted: '',
      version: '',
      comments: '',
      owner_maintainer: '',
      created_at: '',
      updated_at: '',
      process_type: { id: null, name: null },
      business_process_l3: { id: null, name: null },
      group: {
        id: getQueryValue('group'),
        name: null
      },
      division: {
        id: getQueryValue('division'),
        name: null
      },
      region: {
        id: getQueryValue('region'),
        name: null
      },
      legal_entity: {
        id: getQueryValue('legal_entity'),
        name: null
      },
      business_unit: {
        id: getQueryValue('business_unit'),
        name: null
      },
      process_criticality: { id: null, name: null }
    };

    trackingSetProcesses([...processes, newRow]);
    setNewProcessIds((prev) => new Set([...prev, newId]));
  };

  const preparePayload = (processesPerRow: ProcessRow[]) => {
    return {
      data_list: processesPerRow.map((process) => ({
        id: process.id,
        title: process.title || null,
        description: process.description || null,
        description_1: process.description1 || null,
        description_2: process.description2 || null,
        process_image: process.process_image || null,
        process_risk: process.process_risk || null,
        start_date: process.start_date || null,
        end_date: process.end_date || null,
        activation_date: process.activation_date || null,
        process_type_id: process.process_type?.id ?? null,
        responsible_person: process.responsible_person || null,
        business_process_l3_id: process.business_process_l3?.id ?? null,
        group_id: process.group?.id ?? null,
        division_id: process.division?.id ?? null,
        region_id: process.region?.id ?? null,
        legal_entity_id: process.legal_entity?.id ?? null,
        business_unit_id: process.business_unit?.id ?? null,
        process_criticality_id: process.process_criticality?.id ?? null,
        entity_id: selectedEntityId,
        comments: process.comments || null,
        owner_maintainer: process.owner_maintainer || null
      }))
    };
  };

  const handleProcessSave = async () => {
    if (!isDirty && newProcessIds.size === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    const changedProcesses = processes.filter((process) => {
      if (newProcessIds.has(process.id)) return true;
      const original = originalProcessesRef.current.find(
        (p) => p.id === process.id
      );
      if (!original) return false;

      const fieldsToCheck: (keyof ProcessRow)[] = [
        'title',
        'description',
        'responsible_person',
        'start_date',
        'end_date',
        'activation_date',
        'version',
        'comments',
        'owner_maintainer'
      ];
      let hasChanges = false;

      fieldsToCheck.forEach((field) => {
        if (process[field] !== original[field]) {
          hasChanges = true;
        }
      });

      // Check nested objects
      const nestedObjectFields = Object.keys(process).filter((key) => {
        const value = process[key as keyof ProcessRow];
        return (
          typeof value === 'object' &&
          value !== null &&
          typeof value === 'object' &&
          'id' in value
        );
      }) as (keyof ProcessRow)[];

      nestedObjectFields.forEach((field) => {
        if (
          JSON.stringify(process[field]) !== JSON.stringify(original[field])
        ) {
          hasChanges = true;
        }
      });

      return hasChanges;
    });

    if (changedProcesses.length === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    const invalidProcesses = changedProcesses.filter(
      (process) => !process.process_type || !process.process_type.id
    );

    if (invalidProcesses.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: `Process type is required for the process(es).`,
        placement: 'topRight',
        duration: 20
      });
      return;
    }

    const payload = preparePayload(changedProcesses);

    try {
      await ApiService.saveProcessBPR(payload);
      const clearedProcesses = processes.map(
        ({ _pinned, ...rest }) => rest as ProcessRow
      );
      setProcesses(clearedProcesses);
      originalProcessesRef.current = JSON.parse(
        JSON.stringify(clearedProcesses)
      );
      setNewProcessIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      notification.success({
        message: 'Processes Saved',
        description: `${changedProcesses.length} process(es) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error occurred';
      const statusCode = error?.response?.status || 'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });
    }
  };
  const isAnyFilterSelected = Boolean(
    filters.group_id ??
      filters.division_id ??
      filters.region_id ??
      filters.legal_entity_id ??
      filters.business_unit_id
  );

  const isDisabled = (currentValue?: number) =>
    isAnyFilterSelected && currentValue === undefined;

  const getFilteredProcesses = () => {
    return processes.filter((process) => {
      return (Object.keys(FILTER_CONFIG) as FilterKey[]).every((key) => {
        const searchValue = searchStates[key].search.trim();
        if (!searchValue) return true;

        const config = FILTER_CONFIG[key];
        const fieldValue = process[config.field];

        if (
          config.nestedField &&
          typeof fieldValue === 'object' &&
          fieldValue !== null &&
          'name' in fieldValue
        ) {
          return fieldValue.name
            ?.toLowerCase()
            .includes(searchValue.toLowerCase());
        }

        return (fieldValue as string)
          ?.toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    });
  };

  const filteredProcesses = getFilteredProcesses();

  return (
    <>
      <Styled.GlobalTooltipStyles />
      <InternalControlLayout
        onSave={handleProcessSave}
        additionalFilters={
          <>
            <Styled.FilterSelect
              placeholder='Select Group'
              value={filters.group_id}
              disabled={isDisabled(filters.group_id)}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  group_id: value as number | undefined
                }));
                setQuery((prev) => ({
                  ...prev,
                  group: value ? [value.toString()] : []
                }));
              }}
              allowClear
            >
              {group.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Styled.FilterSelect>

            <Styled.FilterSelect
              placeholder='Select Division'
              value={filters.division_id}
              disabled={isDisabled(filters.division_id)}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  division_id: value as number | undefined
                }));
                setQuery((prev) => ({
                  ...prev,
                  division: value ? [value.toString()] : []
                }));
              }}
              allowClear
            >
              {division.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Styled.FilterSelect>

            <Styled.FilterSelect
              placeholder='Select Region'
              value={filters.region_id}
              disabled={isDisabled(filters.region_id)}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  region_id: value as number | undefined
                }));
                setQuery((prev) => ({
                  ...prev,
                  region: value ? [value.toString()] : []
                }));
              }}
              allowClear
            >
              {region.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Styled.FilterSelect>

            <Styled.FilterSelect
              placeholder='Select Legal Entity'
              value={filters.legal_entity_id}
              disabled={isDisabled(filters.legal_entity_id)}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  legal_entity_id: value as number | undefined
                }));
                setQuery((prev) => ({
                  ...prev,
                  legal_entity: value ? [value.toString()] : []
                }));
              }}
              allowClear
            >
              {legal_entity.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Styled.FilterSelect>

            <Styled.LastFilterSelect
              placeholder='Select Business Unit'
              value={filters.business_unit_id}
              disabled={isDisabled(filters.business_unit_id)}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  business_unit_id: value as number | undefined
                }));
                setQuery((prev) => ({
                  ...prev,
                  business_unit: value ? [value.toString()] : []
                }));
              }}
              allowClear
            >
              {business_unit.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Styled.LastFilterSelect>
          </>
        }
      >
        <Styled.ContentWrapper>
          <CustomTable
            drawTable={true}
            columns={risksColumns}
            dataSource={filteredProcesses}
            itemsCount={filteredProcesses.length}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredProcesses.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page: number, size: number) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              }
            }}
            scroll={{ x: 2500 }}
          />
          <Styled.AddRowArea>
            <Button type='primary' onClick={handleAddProcessRow}>
              Add new level 4 process
            </Button>
          </Styled.AddRowArea>
        </Styled.ContentWrapper>
      </InternalControlLayout>
    </>
  );
};

export default Processes;
